import { isNil } from 'lodash';
import { z } from 'zod';
import { QUOTE_AGENT_SETTING_BOUNDS } from '../constants';
import {
  DEFAULT_MARGINAL_FROM_RATIO,
  DEFAULT_PROFITABLE_FROM_RATIO,
  QUOTE_CONTRIBUTION_CUTOFF_BOUNDS,
} from '../quote-contribution-ratio';
import {
  DEFAULT_GENERATED_APPOINTMENT_EARLY_CUSHION_MINUTES,
  DEFAULT_GENERATED_APPOINTMENT_LATE_CUSHION_MINUTES,
} from '../stops';

/**
 * The enum value lists the config schema validates against, declared as
 * literal tuples rather than imported.
 *
 * This package cannot import `@prisma/client`, and the GraphQL-generated
 * enums in `src/generated/graphql.ts` are TypeScript `enum` declarations
 * whose members are nominally distinct from the plain string-literal unions
 * Prisma 5 emits. Literal tuples produce the same string-literal unions
 * Prisma does, so a parsed config value and a Prisma column value remain
 * assignable to each other in both directions.
 *
 * `backend/src/resources/companies/schemas/company-configs.spec.ts` asserts
 * each list equals the corresponding Prisma enum's values or, for the quote
 * agent lists, the backend enum registered from it.
 */
export const COMPANY_CONFIG_ENUM_VALUES = {
  AppointmentSchedulerModel: ['SCHEDULING_ZONE', 'SCHEDULING_RESOURCE'],
  DocumentType: [
    'BILL_OF_LADING',
    'DELIVERY_ALERT',
    'DELIVERY_RECEIPT',
    'DELIVERY_SIGNATURE',
    'PICKUP_ALERT',
    'PICKUP_SIGNATURE',
    'PROOF_OF_DELIVERY',
    'PROOF_OF_DELIVERY_SCANNED',
    'DIGITAL_PROOF_OF_DELIVERY',
    'SHIPMENT_PHOTO',
    'OTHER',
    'MAWB',
    'HAWB',
    'COMMERCIAL_INVOICE',
    'MANIFEST',
    'BACKUP_EMAIL',
    'CHECK_COPY',
    'TRANSFER_MANIFEST',
    'OVERNIGHT_SLIP',
    'ARRIVAL_NOTICE',
    'HAZMAT',
    'VOIDED_CHECK',
    'DAMAGE_REPORT',
    'REJECTION',
    'ABI_NOTES',
    'TRANSPORT_APPROVAL_CERTIFICATE',
    'ITEMIZED_TRANSPORT_DOCUMENT',
    'RECOVERY_DOCUMENT',
    'LABELS',
    'COVER_PAGE',
    'END_OF_DAY_DOCUMENT',
    'CUSTOM_DRIVER_FORM_SIGNATURE',
    'POD_REPORT',
    'ACCOUNTING_REPORT',
    'ORDER_DOCUMENT',
    'STORAGE_UNIT_PHOTO',
    'OSD_PHOTO',
    'PICKUP_RECEIPT_FOR_ESIGN',
    'DIGITAL_PROOF_OF_PICKUP',
    'DOCUMENT_7512',
    'POD_BUNDLE',
    'CUSTOMS_RELEASE',
    'ACE',
    'PREPAID_RECEIPT',
    'DELIVERY_ORDER',
    'PICKUP_ORDER',
    'PARTNER_DOCUMENT',
  ],
  FuelBillingMethod: ['PERCENTAGE', 'AUTO_CALCULATE', 'FLAT_RATE', 'NONE'],
  OrderIdentifier: ['ORDER_NAME', 'SHIPPER_BILL_OF_LADING_NUMBER'],
  OrderNamingScheme: ['CONTACT_ORDINAL', 'COMPANY_ORDINAL'],
  PieceStatus: [
    'AWAITING',
    'ARRIVED',
    'PLANNED',
    'LOADED',
    'DEPARTED',
    'PICKED_UP',
    'ARRIVED_AT_DESTINATION',
    'DELIVERED',
  ],
  QuoteAgentPolicyMode: ['REVIEW_ALL', 'AUTO_SEND_BELOW', 'AUTO_SEND'],
  QuoteAgentReplyIdentity: ['TENET_DOMAIN', 'TENANT_MAILBOX'],
  SameDayDispatchCellHeader: ['SERVICE_LEVEL', 'BILL_OF_LADING_NUMBER'],
  StandardStopType: ['COMMERCIAL', 'RESIDENTIAL', 'DUMMY'],
} as const;

export type CompanyConfigDocumentType =
  (typeof COMPANY_CONFIG_ENUM_VALUES.DocumentType)[number];

/**
 * Document types a company setting may name. `POD_BUNDLE` is a generated
 * SFTP artifact: T2T propagation never copies one, and an invoice requiring
 * one could never be satisfied. Rejected at runtime rather than by narrowing
 * the enum, so the inferred type stays assignable to Prisma's `DocumentType`.
 */
const configurableDocumentTypes = z
  .array(z.enum(COMPANY_CONFIG_ENUM_VALUES.DocumentType))
  .refine((documentTypes) => !documentTypes.includes('POD_BUNDLE'), {
    message: 'POD_BUNDLE cannot be selected in a company setting',
  });

export type CompanyConfigQuoteAgentPolicyMode =
  (typeof COMPANY_CONFIG_ENUM_VALUES.QuoteAgentPolicyMode)[number];

export type CompanyConfigQuoteAgentReplyIdentity =
  (typeof COMPANY_CONFIG_ENUM_VALUES.QuoteAgentReplyIdentity)[number];

export type CompanyConfigPieceStatus =
  (typeof COMPANY_CONFIG_ENUM_VALUES.PieceStatus)[number];

/**
 * Piece statuses `pieceTransferableStatuses` refuses. A piece in one of them
 * has finished its journey, so putting it on a transfer would move freight the
 * system already considers handed over. `CompanyConfigurationsService.update`
 * rejects an input containing either.
 */
export const NON_TRANSFERABLE_PIECE_STATUSES: readonly CompanyConfigPieceStatus[] =
  ['ARRIVED_AT_DESTINATION', 'DELIVERED'];

// Storing these default values as constants so they can be used to construct the old configs objects as well (these values are not nullable in the new configs model but are in the old model)
// If we don't apply defaults, comparing the old and new configs is not viable.
export const DEFAULT_STARTING_INVOICE_ORDINAL_NUMBER = 1;
export const DEFAULT_FUEL_SETTLEMENT_PERCENTAGE_RATE = 100;

const isDecimalLike = (value: unknown): value is { toNumber: () => number } =>
  typeof value === 'object' &&
  !isNil(value) &&
  'toNumber' in value &&
  typeof value.toNumber === 'function';

/**
 * A Postgres `numeric` money rate, as a plain number.
 *
 * It reaches the schema three ways: a JSON number from the blob, a
 * `Prisma.Decimal` from backend callers (both DTOs declare these fields as
 * `Decimal`), and `null` when the API clears the setting. `null` and a blank
 * string mean "not set" and resolve to `undefined`, which is what the
 * pre-`cashew-shared` schema did. Everything that is not a finite number, a
 * numeric string or a `Decimal` is rejected rather than coerced:
 * `z.coerce.number()` alone turns `null`, `''`, `false` and `[]` into `0`,
 * which on a money field is a silent write.
 */
const moneyRate = <Inner extends z.ZodTypeAny>(inner: Inner) =>
  z.preprocess((value) => {
    if (isNil(value) || (typeof value === 'string' && value.trim() === '')) {
      return;
    }
    if (typeof value === 'string') {
      const asNumber = Number(value);
      return Number.isFinite(asNumber) ? asNumber : value;
    }
    if (isDecimalLike(value)) {
      return value.toNumber();
    }
    return value;
  }, inner);

/**
 * The declared field for every company configuration key, before the per-key
 * fallback is applied. `parseCompanyConfigs` parses against this to find out
 * which keys a company's blob would have failed on.
 */
const COMPANY_CONFIG_FIELDS = {
  sameDayDispatchCellHeader: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.SameDayDispatchCellHeader)
    .optional()
    .default('SERVICE_LEVEL'),
  sendSummaryEmailWithIncompleteRoutes: z.boolean().optional().default(false),
  startingInvoiceOrdinalNumber: z
    .number()
    .optional()
    .default(DEFAULT_STARTING_INVOICE_ORDINAL_NUMBER),
  debounceAccessorialSearch: z.boolean().optional().default(false),
  debounceContactSearch: z.boolean().optional().default(false),
  defaultFuelSurchargeBillingMethod: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.FuelBillingMethod)
    .optional()
    .default('PERCENTAGE'),
  defaultDimFactor: z.number().optional().default(250),
  defaultFuelSurcharge: moneyRate(z.number().finite().optional()),
  defaultFuelSettlementPercentageRate: moneyRate(
    z
      .number()
      .finite()
      .optional()
      .default(DEFAULT_FUEL_SETTLEMENT_PERCENTAGE_RATE),
  ),
  defaultRouteStartTime: z.string().datetime().optional(),
  defaultRouteLoadTimeInMinutes: z.number().optional(),
  defaultRouteUnloadTimeInMinutes: z.number().optional(),
  defaultGenerateAppointmentDurationInMinutes: z
    .number()
    .optional()
    .default(DEFAULT_GENERATED_APPOINTMENT_LATE_CUSHION_MINUTES),
  defaultGenerateAppointmentEarlyCushionInMinutes: z
    .number()
    .optional()
    .default(DEFAULT_GENERATED_APPOINTMENT_EARLY_CUSHION_MINUTES),
  enableConsolidatedOrders: z.boolean().optional().default(true),
  showChargesOnCustomerPortal: z.boolean().optional().default(true),
  textNextCustomerForAppointments: z.boolean().optional().default(true),
  invoicePdfLogoUrl: z.string().optional(),
  // Nullable as well as optional because clearing the setting sends an explicit
  // null, and null is what the column holds for every company that has never
  // set one. `.optional()` alone accepts undefined but not null.
  reviewUrl: z.string().nullable().optional(),
  showManuallyConfirmedByDefault: z.boolean().optional().default(false),
  allowDriverToSelectNextStop: z.boolean().optional().default(false),
  showHelpersColumnInDispatch: z.boolean().optional().default(false),
  showVehicleInDailyDispatch: z.boolean().optional().default(false),
  requireReviewForEdiOrderCreation: z.boolean().optional().default(true),
  autoApplyWaitTimeMinutes: z.boolean().optional().default(true),
  useJournalNumberForInvoice: z.boolean().optional().default(false),
  getDriverLocationDataFromMobile: z.boolean().optional().default(false),
  mobilePollingIntervalInSeconds: z.number().optional(),
  requireDriverToAcknowledgeRouteChange: z.boolean().optional().default(true),
  useDriverNumberForDispatch: z.boolean().optional().default(false),
  defaultToAccessorial: z.boolean().optional().default(true),
  enablePaymentMethod: z.boolean().optional().default(false),
  driversShouldMarkFreightAsLoaded: z.boolean().optional().default(false),
  allCapsInOrderPage: z.boolean().optional().default(true),
  orderNamingScheme: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.OrderNamingScheme)
    .optional()
    .default('CONTACT_ORDINAL'),
  companyOrdinalStart: z.number().optional(),
  minDigitsInCompanyOrdinalOrderName: z.number().optional(),
  restrictEditingOrderName: z.boolean().optional().default(false),
  showKilogramsAndCentimeters: z.boolean().optional().default(true),
  showDriverSettlementInBilling: z.boolean().optional().default(true),
  notificationsEnabled: z.boolean().optional().default(true),
  requireDimsToFinalizeCharges: z.boolean().optional().default(false),
  orderIdentifierForCoverSheet: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.OrderIdentifier)
    .optional()
    .default('SHIPPER_BILL_OF_LADING_NUMBER'),
  enableDigitalSignatureFlow: z.boolean().optional().default(false),
  requireDriverSignature: z.boolean().optional().default(false),
  useSeparateDigitalSignatureStep: z.boolean().optional().default(false),
  sendSignedPodEmailAfterLateSignature: z.boolean().optional().default(false),
  requireSignedPodToCompleteStop: z.boolean().optional().default(false),
  includeHyphensInInvoiceName: z.boolean().optional().default(false),
  includeINVInInvoiceName: z.boolean().optional().default(true),
  appointmentSchedulingSlotHours: z.number().optional().default(8),
  appointmentSchedulingLeadingBufferHours: z.number().optional().default(24),
  appointmentSchedulingCutoffDays: z.number().optional().default(10),
  appointmentChangeCutoffHours: z.number().optional().default(12),
  appointmentSelfRescheduleLimit: z.number().int().optional().default(1),
  requireOnHandBeforeSelfScheduling: z.boolean().optional().default(false),
  appointmentSchedulerModel: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.AppointmentSchedulerModel)
    .nullable()
    .optional(),
  createRecurringOrdersInAdvanceHours: z.number().optional().default(0),
  serviceLevelOptional: z.boolean().optional().default(false),
  addOrdersToOpenInvoices: z.boolean().optional().default(false),
  invoiceNotes: z.string().optional(),
  defaultInvoiceDocumentsRequired: configurableDocumentTypes
    .optional()
    .default([]),
  autoMatchEodDocumentsToOrders: z.boolean().optional().default(true),
  requirePODPhotoAndName: z.boolean().optional().default(true),
  requirePODPhotoAndNameForPickups: z.boolean().optional().default(false),
  requirePODPhotoForPickups: z.boolean().optional().default(false),
  requireSigneeNameForPickups: z.boolean().optional().default(false),
  requirePODPhotoForDeliveries: z.boolean().optional().default(true),
  requireSigneeNameForDeliveries: z.boolean().optional().default(true),
  autoGroupStops: z.boolean().optional().default(true),
  requireDriverToConfirmStopCompletedAtTime: z
    .boolean()
    .optional()
    .default(false),
  packageWarehouseLocationsEnabled: z.boolean().optional().default(false),
  wmsEnabled: z.boolean().optional().default(false),
  wmsStorageOrdersEnabled: z.boolean().optional().default(false),
  wmsShortTermStorageEnabled: z.boolean().optional().default(true),
  wmsAutoMarkOnHand: z.boolean().optional().default(false),
  useHawbForLotLabelBarcode: z.boolean().optional().default(false),
  // Where a quote's contribution ratio turns Marginal and then Profitable on
  // the quote page. See `shared/quote-contribution-ratio` for the defaults.
  quoteContributionMarginalFromRatio: z
    .number()
    .finite()
    .min(QUOTE_CONTRIBUTION_CUTOFF_BOUNDS.minimum)
    .max(QUOTE_CONTRIBUTION_CUTOFF_BOUNDS.maximum)
    .optional()
    .default(DEFAULT_MARGINAL_FROM_RATIO),
  quoteContributionProfitableFromRatio: z
    .number()
    .finite()
    .min(QUOTE_CONTRIBUTION_CUTOFF_BOUNDS.minimum)
    .max(QUOTE_CONTRIBUTION_CUTOFF_BOUNDS.maximum)
    .optional()
    .default(DEFAULT_PROFITABLE_FROM_RATIO),
  quoteAgentEnabled: z.boolean().optional().default(false),
  quoteAgentDefaultMode: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.QuoteAgentPolicyMode)
    .optional()
    .default('REVIEW_ALL'),
  quoteAgentAutoSendMaxCents: z
    .number()
    .int()
    .min(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentAutoSendMaxCents.minimum)
    .max(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentAutoSendMaxCents.maximum)
    .nullable()
    .optional(),
  quoteAgentMinConfidence: z
    .number()
    .int()
    .min(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentMinConfidence.minimum)
    .max(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentMinConfidence.maximum)
    .optional()
    .default(95),
  quoteAgentValidityHours: z
    .number()
    .int()
    .min(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentValidityHours.minimum)
    .max(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentValidityHours.maximum)
    .optional()
    .default(48),
  quoteAgentReplyIdentity: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.QuoteAgentReplyIdentity)
    .optional()
    .default('TENET_DOMAIN'),
  quoteAgentDailyModelBudgetCents: z
    .number()
    .int()
    .min(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentDailyModelBudgetCents.minimum)
    .max(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentDailyModelBudgetCents.maximum)
    .optional()
    .default(500),
  defaultPalletLength: z.number().int().nullable().optional(),
  defaultPalletWidth: z.number().int().nullable().optional(),
  defaultPalletHeight: z.number().int().nullable().optional(),
  terminalsEnabled: z.boolean().optional().default(false),
  lineHaulEnabled: z.boolean().optional().default(false),
  lineHaulBuildAheadDays: z.number().int().optional().default(3),
  lineHaulMobilePastWindowHours: z.number().int().optional().default(24),
  lineHaulMobileFutureWindowHours: z.number().int().optional().default(48),
  lineHaulPlanningEnabled: z.boolean().optional().default(false),
  logisticsSectionEnabled: z.boolean().optional().default(false),
  fulfillmentTypeEnabled: z.boolean().optional().default(false),
  pointToPointTariffsEnabled: z.boolean().optional().default(false),
  recoveryTariffsEnabled: z.boolean().optional().default(false),
  showCompanyNameOnBillOfLadingDoc: z.boolean().optional().default(false),
  defaultStandardStopType: z
    .enum(COMPANY_CONFIG_ENUM_VALUES.StandardStopType)
    .optional()
    .default('RESIDENTIAL'),
  orderLevelOsdEnabled: z.boolean().optional().default(true),
  markOnHandUponPickup: z.boolean().optional().default(false),
  showAccessorialsToDrivers: z.boolean().optional().default(true),
  pieceTransferableStatuses: z
    .array(z.enum(COMPANY_CONFIG_ENUM_VALUES.PieceStatus))
    .optional()
    .default(['ARRIVED', 'PLANNED']),
  t2tDelegationEnabled: z.boolean().optional().default(false),
  t2tSyncEnabled: z.boolean().optional().default(false),
  /**
   * Security control: restricts which document types the T2T
   * (tenant-to-tenant) document propagation flow is allowed to copy across
   * the company boundary. Undefined/omitted means "use the operational
   * default" — see {@link DEFAULT_T2T_DOC_PROPAGATION_ALLOWLIST} and
   * {@link getEffectiveT2tDocumentPropagationAllowlist}. An empty array is
   * also treated as "use the default" rather than "propagate nothing", so
   * that clearing the field in the UI cannot accidentally be read as a
   * silent full block.
   */
  t2tDocumentPropagationAllowlist: configurableDocumentTypes.optional(),
};

/**
 * Wraps every field so a value the field rejects resolves to that field's
 * declared default (or `undefined` when it has none) instead of failing the
 * parse.
 *
 * Without this a single bad value in `Company.configs` fails the whole object
 * and sends the company to a fallback path; with it, the blast radius of a bad
 * value is the one key that holds it.
 */
const withPerKeyFallbacks = <Shape extends z.ZodRawShape>(
  shape: Shape,
): { [Key in keyof Shape]: z.ZodCatch<Shape[Key]> } => {
  const caught = Object.entries(shape).map(([key, field]) => {
    const parsedWithNoValue: z.SafeParseReturnType<unknown, unknown> =
      field.safeParse(undefined);
    return [
      key,
      field.catch(
        parsedWithNoValue.success ? parsedWithNoValue.data : undefined,
      ),
    ];
  });
  return Object.fromEntries(caught) as {
    [Key in keyof Shape]: z.ZodCatch<Shape[Key]>;
  };
};

const COMPANY_CONFIG_SHAPE = withPerKeyFallbacks(COMPANY_CONFIG_FIELDS);

/**
 * @description Represents the shape of the JSON object representing the configurations for a particular client.
 */
export const COMPANY_CONFIGS_SCHEMA = z.object(COMPANY_CONFIG_SHAPE);

/**
 * The same fields without the per-key fallback, so a rejected value surfaces
 * as a zod issue instead of being swallowed.
 */
const STRICT_COMPANY_CONFIGS_SCHEMA = z.object(COMPANY_CONFIG_FIELDS);

export type CompanyConfigs = z.infer<typeof COMPANY_CONFIGS_SCHEMA>;

export type CompanySettingKey = keyof CompanyConfigs;

export const COMPANY_SETTING_KEYS: readonly CompanySettingKey[] = Object.keys(
  COMPANY_CONFIGS_SCHEMA.shape,
) as CompanySettingKey[];

/**
 * Setting keys an explicit `null` clears. A key resolves `null` to "no value"
 * only when it is nullable or has no default; for every other key `null` names
 * no value to store and the update ignores it, so an editor that sends `null`
 * for one of those reports a save the server did not make.
 *
 * Derived here rather than in either writer so the API's rule and the editors'
 * rule cannot drift: `buildConfigPatch` decides what to clear from this set,
 * and every surface offering a clear affordance gates it on the same one.
 *
 * `safeParse` rather than `parse` because a field declared without the per-key
 * `.catch()` wrapper would otherwise throw at module load and take the process
 * down on boot rather than at the call site.
 */
export const CLEARABLE_SETTING_KEYS: ReadonlySet<CompanySettingKey> = new Set(
  COMPANY_SETTING_KEYS.filter((key) => {
    const parsed = COMPANY_CONFIGS_SCHEMA.shape[key].safeParse(null);
    return parsed.success && isNil(parsed.data);
  }),
);

export const QUOTE_AGENT_CEILING_REQUIRED_MESSAGE =
  'The reply policy "Send on its own below the ceiling" needs an auto-send ceiling';

/**
 * Whether a configuration picks the send-below-the-ceiling policy without a
 * ceiling, which would leave no reply eligible to send. A predicate rather
 * than a schema refinement: `parseCompanyConfigs` never throws and a stored
 * blob must keep parsing, so only writers apply it, to the merged result.
 */
export const violatesQuoteAgentCeilingRequired = (
  configs: Pick<
    CompanyConfigs,
    'quoteAgentDefaultMode' | 'quoteAgentAutoSendMaxCents'
  >,
): boolean =>
  configs.quoteAgentDefaultMode === 'AUTO_SEND_BELOW' &&
  isNil(configs.quoteAgentAutoSendMaxCents);

/**
 * Parses a company's raw `configs` JSON. Never throws: a value no key accepts
 * resolves to that key's default, and a `raw` that is not a JSON object is
 * read as an empty object.
 *
 * @param onRejected Called once per key whose stored value was rejected, with
 *   the value that was rejected. Supplying it costs a second parse, so callers
 *   that do not report rejections should omit it.
 */
export function parseCompanyConfigs(
  raw: unknown,
  onRejected?: (key: CompanySettingKey, rejectedValue: unknown) => void,
): CompanyConfigs {
  const source: Record<string, unknown> =
    typeof raw === 'object' && !isNil(raw) && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};

  if (onRejected !== undefined) {
    const strictResult = STRICT_COMPANY_CONFIGS_SCHEMA.safeParse(source);
    if (!strictResult.success) {
      const reportedKeys = new Set<string>();
      for (const issue of strictResult.error.issues) {
        const key = issue.path[0];
        if (typeof key !== 'string' || reportedKeys.has(key)) {
          continue;
        }
        reportedKeys.add(key);
        onRejected(key as CompanySettingKey, source[key]);
      }
    }
  }

  return COMPANY_CONFIGS_SCHEMA.parse(source);
}

/**
 * Security control: the default set of document types the T2T document
 * propagation flow is allowed to copy across the company boundary when a
 * company has not configured an explicit `t2tDocumentPropagationAllowlist`.
 *
 * This list is operational-document-only by design. It deliberately excludes
 * every financial, accounting, and internal-only document type (e.g.
 * `COMMERCIAL_INVOICE`, `CHECK_COPY`, `VOIDED_CHECK`, `ACCOUNTING_REPORT`,
 * `POD_REPORT`, `BACKUP_EMAIL`, `ABI_NOTES`, `COVER_PAGE`,
 * `END_OF_DAY_DOCUMENT`, `ORDER_DOCUMENT`, `OTHER`) so that a company cannot
 * leak sensitive or internal documents to another tenant simply by having
 * T2T propagation enabled with no explicit configuration.
 *
 * Do not add a document type here without confirming it contains no
 * financial, accounting, or internal-only data — this list is a tenant
 * isolation boundary, not a convenience default.
 */
export const DEFAULT_T2T_DOC_PROPAGATION_ALLOWLIST: CompanyConfigDocumentType[] =
  [
    'BILL_OF_LADING',
    'DELIVERY_ALERT',
    'DELIVERY_RECEIPT',
    'DELIVERY_SIGNATURE',
    'PICKUP_ALERT',
    'PICKUP_SIGNATURE',
    'PROOF_OF_DELIVERY',
    'PROOF_OF_DELIVERY_SCANNED',
    'DIGITAL_PROOF_OF_DELIVERY',
    'SHIPMENT_PHOTO',
    'MAWB',
    'HAWB',
    'MANIFEST',
    'TRANSFER_MANIFEST',
    'ARRIVAL_NOTICE',
    'HAZMAT',
    'LABELS',
    'DAMAGE_REPORT',
    'REJECTION',
    'OSD_PHOTO',
    'STORAGE_UNIT_PHOTO',
    'DIGITAL_PROOF_OF_PICKUP',
    'PICKUP_RECEIPT_FOR_ESIGN',
    'OVERNIGHT_SLIP',
    'RECOVERY_DOCUMENT',
    'TRANSPORT_APPROVAL_CERTIFICATE',
    'ITEMIZED_TRANSPORT_DOCUMENT',
    'DOCUMENT_7512',
    'CUSTOM_DRIVER_FORM_SIGNATURE',
  ];

/**
 * Security control: resolves the effective T2T document propagation
 * allowlist for a company from its parsed {@link CompanyConfigs}.
 *
 * Returns the company's configured `t2tDocumentPropagationAllowlist` when it
 * is present and non-empty; otherwise falls back to
 * {@link DEFAULT_T2T_DOC_PROPAGATION_ALLOWLIST}. Callers in the T2T document
 * propagation flow must gate every cross-tenant document copy through this
 * helper's return value rather than reading `configs.t2tDocumentPropagationAllowlist`
 * directly, so that the "no configuration" and "empty array" cases can never
 * be misread as "propagate everything".
 *
 * @param configs The company's parsed configs (see {@link COMPANY_CONFIGS_SCHEMA}).
 * @returns The document types allowed to propagate cross-tenant for this company.
 */
export function getEffectiveT2tDocumentPropagationAllowlist(
  configs: Pick<CompanyConfigs, 't2tDocumentPropagationAllowlist'>,
): CompanyConfigDocumentType[] {
  const configured = configs.t2tDocumentPropagationAllowlist;
  return configured !== undefined && configured.length > 0
    ? configured
    : DEFAULT_T2T_DOC_PROPAGATION_ALLOWLIST;
}
